import { humanize, parameterize } from 'inflected';
import { CreatorAttributeFilters } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import { YouTubeCreatorSetWithMembers } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import {
  CreatorSetAudienceAttribute,
  YouTubeCreatorSetMember
} from '@blisspointmedia/bpm-types/dist/pgTables/YoutubeCreatorSetMember';
import { MenuItem } from '@Components/Dropdown/dropdown-constants';
import { Sheet } from '@src/utils/download-utils';
import {
  ACTIVE_SHORTS_BADGE_LABEL,
  AGE_RANGE_OPTIONS,
  DEVICE_OPTIONS,
  GENDER_OPTIONS,
  INCOME_RANGE_OPTIONS,
  PARENTAL_STATUS_OPTIONS,
  SEARCH_MODE_OPTIONS,
  ViewerDetails
} from '../Discover/creator-discovery-constants';
import {
  AudienceDimension,
  DEMOGRAPHIC_LABELS,
  EXPORT_AUDIENCE_NOTE,
  EXPORT_PARTNERSHIP_COLUMNS
} from './creator-sets-constants';
import { formatSetDate, parseCount, sharesViewerData } from './creator-sets-utils';

const DIMENSION_LABELS: Record<string, string> = {
  ...DEMOGRAPHIC_LABELS,
  [AudienceDimension.KNOWLEDGE_GRAPH]: 'Topic',
  [AudienceDimension.AFFINITY_USER_INTEREST]: 'Affinity interest',
  [AudienceDimension.IN_MARKET_USER_INTEREST]: 'In-market interest'
};
const DIMENSION_ORDER: string[] = Object.values(AudienceDimension);

const yesNo = (value: boolean): string => (value ? 'Yes' : 'No');

const channelUrl = (channelId: string): string => `https://www.youtube.com/channel/${channelId}`;
export const videoUrl = (videoId: string): string => `https://www.youtube.com/watch?v=${videoId}`;

const optionLabels = <T extends string>(options: MenuItem<T>[], values: T[]): string =>
  values.map(value => options.find(option => option.value === value)?.label ?? value).join(', ');

// The earliest and latest snapshot dates, as one date when they match.
const snapshotRange = (members: YouTubeCreatorSetMember[]): string => {
  const dates = members
    .map(({ insights }) => insights.snapshotted_at)
    .filter(date => Number.isFinite(Date.parse(date)))
    .sort((a, b) => Date.parse(a) - Date.parse(b));
  if (dates.length === 0) return '';
  const first = formatSetDate(dates[0]);
  const last = formatSetDate(dates[dates.length - 1]);
  return first === last ? first : `${first} – ${last}`;
};

// Only the inputs the search used, in the Discover panel's order and labels. Interest ids are
// listed as ids: a saved search keeps no display names.
const attributeSearchRows = ({
  ageRanges = [],
  genders = [],
  parentalStatuses = [],
  incomeRanges = [],
  devices = [],
  entityIds = [],
  categoryIds = [],
  userInterestIds = []
}: CreatorAttributeFilters): [string, string][] =>
  (
    [
      ['Age ranges', optionLabels(AGE_RANGE_OPTIONS, ageRanges)],
      ['Genders', optionLabels(GENDER_OPTIONS, genders)],
      ['Parental status', optionLabels(PARENTAL_STATUS_OPTIONS, parentalStatuses)],
      ['Household income', optionLabels(INCOME_RANGE_OPTIONS, incomeRanges)],
      ['Devices', optionLabels(DEVICE_OPTIONS, devices)],
      ['Topic IDs', entityIds.join(', ')],
      ['Category IDs', categoryIds.join(', ')],
      ['Interest IDs', userInterestIds.join(', ')]
    ] satisfies [string, string][]
  ).filter(([, value]) => value !== '');

const searchRows = (search: YouTubeCreatorSetWithMembers['search_params']): [string, string][] => {
  const mode = SEARCH_MODE_OPTIONS.find(({ key }) => search[key] !== undefined);
  const rows: [string, string][] = [['Search', mode?.label ?? '']];
  if (search.attributes) {
    rows.push(...attributeSearchRows(search.attributes));
  }
  if (search.topicEntityIds) {
    rows.push(['Topic IDs', search.topicEntityIds.join(', ')]);
  }
  if (search.channelIds) {
    rows.push(['Channel IDs', search.channelIds.join(', ')]);
  }
  return rows;
};

const toSetRows = (
  set: YouTubeCreatorSetWithMembers,
  exportedOn: string
): { Field: string; Value: string | number }[] =>
  (
    [
      ['Creator set', set.name],
      ['Description', set.description ?? ''],
      ['Company', set.company],
      ['Creators', set.members.length],
      ['Metrics as of', snapshotRange(set.members)],
      ['Exported on', exportedOn],
      ['Created by', set.created_by],
      ['Created on', formatSetDate(set.created_at)],
      ['Last edited by', set.lastuser],
      ['Last edited on', formatSetDate(set.lastmodified)],
      ['Audience detail', EXPORT_AUDIENCE_NOTE],
      ...searchRows(set.search_params)
    ] satisfies [string, string | number][]
  ).map(([Field, Value]) => ({ Field, Value }));

// Counts are numbers so they sort and sum in Excel; a count the snapshot lacks is left blank.
const toCreatorRow = (member: YouTubeCreatorSetMember) => {
  const { channel_id, insights, added_by, added_at } = member;
  return {
    Creator: insights.display_name,
    'Channel ID': channel_id,
    'Channel URL': channelUrl(channel_id),
    Subscribers: parseCount(insights.subscriber_count),
    'Total views': parseCount(insights.views_count),
    ...Object.fromEntries(
      EXPORT_PARTNERSHIP_COLUMNS.map(({ type, label }) => [
        label,
        yesNo((insights.partnership_opportunities ?? []).includes(type))
      ])
    ),
    [ACTIVE_SHORTS_BADGE_LABEL]: yesNo(insights.is_active_shorts_creator),
    'Viewer details': sharesViewerData(member) ? ViewerDetails.SHARED : ViewerDetails.NOT_SHARED,
    'Metrics as of': formatSetDate(insights.snapshotted_at),
    'Added by': added_by,
    'Added on': formatSetDate(added_at)
  };
};

const dimensionRank = (dimension: string): number => {
  const index = DIMENSION_ORDER.indexOf(dimension);
  return index === -1 ? DIMENSION_ORDER.length : index;
};

// Known dimensions first in a fixed order, then any newer ones; largest share first within each.
const byDimensionThenShare = (a: CreatorSetAudienceAttribute, b: CreatorSetAudienceAttribute) =>
  dimensionRank(a.dimension) - dimensionRank(b.dimension) ||
  a.dimension.localeCompare(b.dimension) ||
  (b.viewerShare ?? 0) - (a.viewerShare ?? 0);

const toAudienceRows = ({ channel_id, insights }: YouTubeCreatorSetMember) =>
  [...(insights.channel_audience_attributes ?? [])].sort(byDimensionThenShare).map(attribute => ({
    Creator: insights.display_name,
    'Channel ID': channel_id,
    Dimension: DIMENSION_LABELS[attribute.dimension] ?? humanize(attribute.dimension),
    Attribute: attribute.displayName,
    'Viewer share (%)':
      attribute.viewerShare === undefined
        ? undefined
        : Math.round(attribute.viewerShare * 1000) / 10
  }));

// Snapshots from before top videos were stored have no rows here.
const toTopVideoRows = ({ channel_id, insights }: YouTubeCreatorSetMember) =>
  (insights.top_videos ?? []).map(video => ({
    Creator: insights.display_name,
    'Channel ID': channel_id,
    Video: video.title,
    'Video URL': videoUrl(video.video_id),
    Views: parseCount(video.views_count),
    Published: formatSetDate(video.publish_date)
  }));

// The set as Excel sheets, built only from the stored snapshots so an export reconciles with what
// the set showed when it was sent, however long ago. `exportedOn` is the export's date, yyyy-MM-dd.
export const toCreatorSetSheets = (
  set: YouTubeCreatorSetWithMembers,
  exportedOn: string
): Sheet[] => [
  { name: 'Set', data: toSetRows(set, exportedOn) },
  { name: 'Creators', data: set.members.map(toCreatorRow) },
  { name: 'Audience', data: set.members.flatMap(toAudienceRows) },
  { name: 'Top videos', data: set.members.flatMap(toTopVideoRows) }
];

// company_creator_set_<set name>_<yyyy-MM-dd>, the shape the plan download uses. The set name is
// reduced to letters, digits and underscores, and dropped if nothing is left.
export const toCreatorSetFileName = (company: string, setName: string, exportedOn: string) =>
  [company, 'creator_set', parameterize(setName, { separator: '_' }), exportedOn]
    .filter(Boolean)
    .join('_');
